import { useState, useEffect, useCallback, useRef } from 'react';
import { moodBoardAPI, MoodBoard } from '../api/mood_boards';
import { useAuth } from '../hooks/useAuth';
import { MOODBOARD_PAGE_SIZE } from '../constants/moodboard';

export type TabType = 'all' | 'saved' | 'my' | 'liked';

interface UseMoodBoardsOptions {
  selectedTab: TabType;
  searchQuery?: string;
}

interface UseMoodBoardsReturn {
  moodBoards: MoodBoard[];
  filteredBoards: MoodBoard[];
  currentDataTab: TabType; // Track which tab the current data belongs to
  loading: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  hasMore: boolean;
  error: string | null;
  loadMoodBoards: (isRefresh?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  handleRefresh: () => void;
  handleLikeMoodBoard: (boardId: number) => Promise<void>;
  handleSaveMoodBoard: (boardId: number) => Promise<{ saved: boolean } | void>;
}

export const useMoodBoards = ({
  selectedTab,
  searchQuery = '',
}: UseMoodBoardsOptions): UseMoodBoardsReturn => {
  const { user } = useAuth();
  const [moodBoards, setMoodBoards] = useState<MoodBoard[]>([]);
  const [currentDataTab, setCurrentDataTab] = useState<TabType>('all');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const isInitialLoad = useRef(true);
  const PAGE_SIZE = MOODBOARD_PAGE_SIZE;

  // Cache for each tab to prevent reloading on tab switch
  const tabCache = useRef<
    Map<
      TabType,
      {
        boards: MoodBoard[];
        offset: number;
        total: number;
        hasMore: boolean;
        timestamp: number;
      }
    >
  >(new Map());
  const CACHE_DURATION = 60000; // 1 minute cache

  useEffect(() => {
    return () => {};
  }, []); // Empty deps = runs once on mount

  const loadMoodBoards = useCallback(
    async (
      isRefresh = false,
      startOffset = 0,
      tab?: TabType,
      skipLoadingState = false
    ) => {
      const targetTab = tab || selectedTab; // Use passed tab or current selectedTab
      try {
        if (isRefresh) {
          setRefreshing(true);
          setOffset(0); // Reset offset on refresh
          setHasMore(true);
        } else {
          // Only set loading to true on initial load, not on tab changes or background refreshes
          // This prevents flickering when switching tabs or refreshing cached data
          if (isInitialLoad.current && !skipLoadingState) {
            setLoading(true);
            setOffset(0); // Reset offset on initial load
            setHasMore(true);
          }
        }
        setError(null);

        let response;

        switch (targetTab) {
          case 'all':
            // Get all public mood boards
            response = await moodBoardAPI.getMoodBoards({
              public_only: true,
              limit: PAGE_SIZE,
              offset: startOffset,
            });
            break;

          case 'saved':
            // Get saved mood boards using dedicated endpoint
            response = await moodBoardAPI.getSavedMoodBoards({
              limit: PAGE_SIZE,
              offset: startOffset,
            });
            break;

          case 'my':
            // Get user's own mood boards
            if (user?.id) {
              response = await moodBoardAPI.getUserMoodBoards(Number(user.id), {
                limit: PAGE_SIZE,
                offset: startOffset,
              });
            } else {
              response = {
                items: [],
                total: 0,
                limit: PAGE_SIZE,
                offset: startOffset,
                has_next: false,
              };
            }
            break;

          case 'liked':
            response = await moodBoardAPI.getLikedMoodBoards({
              limit: PAGE_SIZE,
              offset: startOffset,
            });
            break;

          default:
            response = await moodBoardAPI.getMoodBoards({
              public_only: true,
              limit: PAGE_SIZE,
              offset: startOffset,
            });
        }

        const boards = response.items;

        setHasMore(response.has_next);
        setTotal(response.total);

        const offsetIncrement = boards.length;

        if (isRefresh || isInitialLoad.current) {
          setMoodBoards(boards);
          setCurrentDataTab(targetTab);
          setOffset(offsetIncrement);

          if (!searchQuery) {
            const cacheEntry = {
              boards: boards,
              offset: offsetIncrement,
              total: response.total,
              hasMore: response.has_next,
              timestamp: Date.now(),
            };
            tabCache.current.set(targetTab, cacheEntry);
          }
        } else {
          setMoodBoards(boards);
          setCurrentDataTab(targetTab);
          setOffset(offsetIncrement);
        }

        isInitialLoad.current = false;
      } catch (error: unknown) {
        const err = error as {
          code?: string;
          message?: string;
          response?: { data?: { detail?: string | unknown[] } };
        };
        console.error('❌ Error loading mood boards:', error);
        console.error('❌ Error response:', err.response?.data);
        let errorMessage = 'Failed to load mood boards';

        // Handle timeout errors specifically
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          errorMessage =
            'Request timed out. Please check your connection and try again.';
        } else if (err.response?.data?.detail) {
          // Handle validation errors (array of error objects)
          if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail
              .map((e: unknown) =>
                typeof e === 'object' && e && 'msg' in e
                  ? (e as { msg: string }).msg
                  : e
              )
              .join(', ');
          } else {
            errorMessage = String(err.response.data.detail);
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        // Only clear data on error if it's the initial load to prevent flickering
        if (isInitialLoad.current) {
          setMoodBoards([]);
          setCurrentDataTab(targetTab); // Keep data tab in sync even on error
        }
        isInitialLoad.current = false;
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedTab, searchQuery, user?.id, PAGE_SIZE]
  );

  const handleRefresh = useCallback(() => {
    // Clear cache for current tab on manual refresh
    tabCache.current.delete(selectedTab);
    loadMoodBoards(true, 0, selectedTab);
  }, [selectedTab, loadMoodBoards]);

  const loadMore = useCallback(async () => {
    // Don't load more if already loading, no more items, or initial load
    if (loadingMore || !hasMore || loading || refreshing) {
      return;
    }

    try {
      setLoadingMore(true);
      setError(null);

      const nextOffset = offset;

      let response;

      switch (selectedTab) {
        case 'all':
          response = await moodBoardAPI.getMoodBoards({
            public_only: true,
            limit: PAGE_SIZE,
            offset: nextOffset,
          });
          break;

        case 'saved':
          response = await moodBoardAPI.getSavedMoodBoards({
            limit: PAGE_SIZE,
            offset: nextOffset,
          });
          break;

        case 'my':
          if (user?.id) {
            response = await moodBoardAPI.getUserMoodBoards(Number(user.id), {
              limit: PAGE_SIZE,
              offset: nextOffset,
            });
          } else {
            response = {
              items: [],
              total: 0,
              limit: PAGE_SIZE,
              offset: nextOffset,
              has_next: false,
            };
          }
          break;

        case 'liked':
          response = await moodBoardAPI.getLikedMoodBoards({
            limit: PAGE_SIZE,
            offset: nextOffset,
          });
          break;

        default:
          response = await moodBoardAPI.getMoodBoards({
            public_only: true,
            limit: PAGE_SIZE,
            offset: nextOffset,
          });
      }

      const boards = response.items;

      setHasMore(response.has_next);
      setTotal(response.total);

      if (boards.length > 0) {
        setMoodBoards((prev) => {
          const newBoards = [...prev, ...boards];

          if (!searchQuery) {
            tabCache.current.set(selectedTab, {
              boards: newBoards,
              offset: offset + boards.length,
              total: response.total,
              hasMore: response.has_next,
              timestamp: Date.now(),
            });
          }

          return newBoards;
        });
        setOffset((prev) => prev + boards.length);
      } else {
        setHasMore(false);
      }
    } catch (error: unknown) {
      console.error('❌ Error loading more mood boards:', error);
      // Don't show error for pagination failures, just stop loading more
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
    // searchQuery not needed - loadMore fetches raw data; filtering happens in filteredBoards
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchQuery used only for filtering, not for API fetch
  }, [
    selectedTab,
    offset,
    hasMore,
    loadingMore,
    loading,
    refreshing,
    user?.id,
    PAGE_SIZE,
  ]);

  const handleLikeMoodBoard = async (boardId: number) => {
    try {
      if (!user) {
        return;
      }

      const response = await moodBoardAPI.toggleLike(boardId);

      setMoodBoards((prevBoards) =>
        prevBoards.map((board) =>
          board.id === boardId
            ? {
                ...board,
                likes_count: response.likes_count,
                is_liked: response.liked,
              }
            : board
        )
      );

      console.log(`🔄 CACHE UPDATE - Like action (liked=${response.liked})`);
      tabCache.current.forEach((cached, tab) => {
        const updatedBoards = cached.boards.map((board) =>
          board.id === boardId
            ? {
                ...board,
                likes_count: response.likes_count,
                is_liked: response.liked,
              }
            : board
        );

        let finalBoards = updatedBoards;
        if (tab === 'liked') {
          if (response.liked) {
            const boardExists = updatedBoards.some((b) => b.id === boardId);
            if (!boardExists) {
              const boardToAdd = moodBoards.find((b) => b.id === boardId);
              if (boardToAdd) {
                finalBoards = [
                  {
                    ...boardToAdd,
                    is_liked: true,
                    likes_count: response.likes_count,
                  },
                  ...updatedBoards,
                ];
              }
            }
          } else {
            finalBoards = updatedBoards.filter((b) => b.id !== boardId);
          }
        }

        tabCache.current.set(tab, {
          ...cached,
          boards: finalBoards,
          timestamp: Date.now(), // Update timestamp to keep cache fresh
        });
      });
    } catch (error: unknown) {
      console.error('Error toggling like:', error);
      const err = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to update like';
      console.error('Like error:', errorMessage);
    }
  };

  const filteredBoards = moodBoards.filter((board) => {
    if (board.is_hidden === true) {
      return false;
    }

    if (currentDataTab === selectedTab) {
      if (selectedTab === 'liked' && !board.is_liked) {
        return false;
      }
    }

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      board.name.toLowerCase().includes(query) ||
      board.description?.toLowerCase().includes(query) ||
      board.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  useEffect(() => {
    const cached = tabCache.current.get(selectedTab);
    const now = Date.now();
    const cacheValid =
      cached && !searchQuery && now - cached.timestamp < CACHE_DURATION;
    const cacheHasItems = cacheValid && cached.boards.length > 0;

    // Use cache only when it has items, so we never flash "no moodboard" from stale empty cache (e.g. after create)
    if (cacheHasItems) {
      setMoodBoards(cached.boards);
      setCurrentDataTab(selectedTab);
      setOffset(cached.offset);
      setTotal(cached.total);
      setHasMore(cached.hasMore);
      setLoading(false);
      setError(null);
      isInitialLoad.current = false;

      if (now - cached.timestamp > 30000) {
        loadMoodBoards(false, 0, selectedTab, true);
      }
    } else {
      // No cache or empty cache: show loading and fetch so we don't flash empty state
      setLoading(true);
      setOffset(0);
      setTotal(0);
      setHasMore(true);
      isInitialLoad.current = true;
      loadMoodBoards(false, 0, selectedTab);
    }
  }, [selectedTab, searchQuery, loadMoodBoards]);

  const handleSaveMoodBoard = async (boardId: number) => {
    try {
      if (!user) {
        return; // Handle this at component level
      }

      const response = await moodBoardAPI.toggleSave(boardId);
      const isSaved = response.liked;

      setMoodBoards((prevBoards) =>
        prevBoards.map((board) =>
          board.id === boardId
            ? {
                ...board,
                is_saved: isSaved,
              }
            : board
        )
      );

      tabCache.current.forEach((cached, tab) => {
        const updatedBoards = cached.boards.map((board) =>
          board.id === boardId ? { ...board, is_saved: isSaved } : board
        );

        let finalBoards = updatedBoards;
        if (tab === 'saved') {
          if (isSaved) {
            const boardExists = updatedBoards.some((b) => b.id === boardId);
            if (!boardExists) {
              const boardToAdd = moodBoards.find((b) => b.id === boardId);
              if (boardToAdd) {
                finalBoards = [
                  { ...boardToAdd, is_saved: true },
                  ...updatedBoards,
                ];
              }
            }
          } else {
            finalBoards = updatedBoards.filter((b) => b.id !== boardId);
          }
        }

        tabCache.current.set(tab, {
          ...cached,
          boards: finalBoards,
          timestamp: Date.now(), // Update timestamp to keep cache fresh
        });
      });

      return { ...response, saved: isSaved };
    } catch (error: unknown) {
      console.error('Error toggling save:', error);
      const err = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMessage =
        err.response?.data?.detail || err.message || 'Failed to update save';
      console.error('Save error:', errorMessage);
      throw error;
    }
  };

  return {
    moodBoards,
    filteredBoards,
    currentDataTab,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    error,
    loadMoodBoards,
    loadMore,
    handleRefresh,
    handleLikeMoodBoard,
    handleSaveMoodBoard,
  };
};
